import { useEffect, useRef, useState } from 'react';
import { CircleCheckBig, CircleX, Loader2 } from 'lucide-react';
import { FullscreenDialog } from '@/components/ui/fullscreen-dialog';
import {
  StyledTable,
  StyledTableWrapper,
  StyledTableHeader,
  StyledTableBody,
  StyledTableRow,
  StyledTableHead,
} from '@/components/ui/styled-table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DisabledReasonTooltip } from '@/components/ui/disabled-reason-tooltip';
import {
  usePendingMemberProposals,
  type PendingProposalWithSignatures,
} from '@/hooks/admin/member/use-pending-member-proposals';
import { usePendingSignaturesState } from '@/hooks/admin/member/use-pending-signatures-state';
import { PendingSignaturesModal } from '../../../components/modals/pending-signatures-modal';
import { startProposalBatchExecution } from '@/components/execution/start-proposal-batch-execution';
import { useProposalExecutionState } from '@/components/execution/use-proposal-execution-state';
import { ExecutionInProgressButton } from '@/components/execution/execution-in-progress-button';
import { MemberProposalRow } from './member-proposal-row';
import { useExecuteMemberProposal } from '@/hooks/admin/member/use-execute-member-proposal';
import { useSelectedInvalidProposal } from '@/hooks/use-selected-invalid-proposal';
import { useSelectedProposalExecutionState } from '@/hooks/use-selected-proposal-execution-state';
import { KeyedMutator } from 'swr';
import type { ProposalExecutionCandidate } from '@/utils/SignatureCountsHelpers';
import {
  OrgOperationDecision,
  type OrgOpSignableDecision,
  type ExecuteAction,
} from '@mls-wallet/shared-types';

interface ReviewMemberChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proposals: PendingProposalWithSignatures[];
  onSignSelected: (
    selectedProposalIds: string[],
    decision: OrgOpSignableDecision
  ) => Promise<void>;
  onApplied: () => void;
  isProposalStateAvailable: boolean;
  onProposalStateUnavailable: () => void;
  refreshProposals: KeyedMutator<PendingProposalWithSignatures[]>;
  isSubmitting: boolean;
}

function toMemberExecutionCandidate(
  proposal: PendingProposalWithSignatures
): ProposalExecutionCandidate {
  return {
    id: proposal.proposal.id,
    status: proposal.proposal.status,
    approvals: proposal.approvals,
    rejections: proposal.rejections,
    requiredApprovals: proposal.requiredApprovals,
    requiredNetworkIds: proposal.requiredNetworkIds,
  };
}

export function ReviewMemberChangesDialog({
  open,
  onOpenChange,
  proposals,
  onSignSelected,
  onApplied,
  isProposalStateAvailable,
  onProposalStateUnavailable,
  refreshProposals,
  isSubmitting,
}: ReviewMemberChangesDialogProps) {
  const [selectedChangeIds, setSelectedChangeIds] = useState<string[]>([]);
  const [executionProposalIds, setExecutionProposalIds] = useState<string[]>(
    []
  );
  const hasObservedExecutionRef = useRef(false);

  const {
    pendingSignableIds,
    setPendingSignableIds,
    pendingDecision,
    setPendingDecision,
    isPendingModalOpen,
    setIsPendingModalOpen,
    resetPendingState,
  } = usePendingSignaturesState();

  const { executeProposal, isLoading: isExecuteLoading } =
    useExecuteMemberProposal();

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedChangeIds((proposals ?? []).map((p) => p.proposal.id));
    } else {
      setSelectedChangeIds([]);
    }
  };

  const handleSelectChange = (changeId: string, checked: boolean) => {
    if (checked) {
      setSelectedChangeIds((prev) => [...prev, changeId]);
    } else {
      setSelectedChangeIds((prev) => prev.filter((id) => id !== changeId));
    }
  };

  const { selectedHasInvalidProposal, approveDisabledReason } =
    useSelectedInvalidProposal(proposals, selectedChangeIds);

  /**
   * Start device signing for the current selection after persisting its pending
   * signature rows. Invalid proposals remain rejectable, but cannot be approved.
   */
  const signSelected = async (decision: OrgOpSignableDecision) => {
    // Approval signatures authorize execution, so reject-only proposals must
    // be stopped before the signing flow starts.
    if (
      decision === OrgOperationDecision.APPROVE &&
      selectedHasInvalidProposal
    ) {
      return;
    }

    const idsToSign = selectedChangeIds.slice();

    try {
      await onSignSelected(idsToSign, decision);
    } catch (error) {
      console.error('Failed to sign proposals:', error);
      // Remove any pending-signature state from an earlier attempt so a failed
      // batch cannot reopen stale modal data.
      resetPendingState();
      return;
    }

    // Device signatures do not exist yet. Refreshing exposes the pending rows
    // to the modal, which can then detect either completion or cancellation.
    await refreshProposals();

    setPendingSignableIds(idsToSign);
    setPendingDecision(decision);
    setIsPendingModalOpen(true);

    setSelectedChangeIds([]);
  };

  // Polling can change readiness after rows are selected. Keep the launch
  // all-or-nothing so one stale or under-signed proposal blocks the batch.
  const {
    selectedProposalIds,
    canExecuteApproval: canExecuteSelected,
    canExecuteRejection: canExecuteRejectionSelected,
  } = useSelectedProposalExecutionState(
    proposals,
    selectedChangeIds,
    toMemberExecutionCandidate
  );
  const proposalIds =
    executionProposalIds.length > 0 ? executionProposalIds : selectedChangeIds;
  const trackedProposalIds = new Set(proposalIds);
  const isServerExecuting = proposals.some(
    ({ proposal, isExecuting }) =>
      trackedProposalIds.has(proposal.id) && isExecuting
  );
  const { isExecuting: isProposalExecuting, startExecution } =
    useProposalExecutionState({
      proposalId: proposalIds,
      isServerExecuting: isServerExecuting || !isProposalStateAvailable,
    });

  useEffect(() => {
    // Retain the batch IDs until an observed local or server execution becomes terminal.
    if (executionProposalIds.length === 0) return;
    // A conflict invalidates cached idle state. Only a usable proposal response
    // can confirm that the competing execution has reached a terminal state.
    if (!isProposalStateAvailable) return;
    if (isProposalExecuting) {
      hasObservedExecutionRef.current = true;
      return;
    }
    if (!hasObservedExecutionRef.current) return;
    hasObservedExecutionRef.current = false;
    setExecutionProposalIds([]);
  }, [executionProposalIds, isProposalExecuting, isProposalStateAvailable]);

  /**
   * Hand the validated batch to the global execution tray. The tray outlives
   * this dialog and reports each proposal separately, so navigation cannot hide
   * a remaining execution or a partial failure.
   */
  const executeSelected = (action: ExecuteAction) => {
    // Case: return when the batch is empty or any selected proposal is already executing.
    if (selectedProposalIds.length === 0 || isProposalExecuting) return;

    // Readiness comes from polled data, so re-check the chosen action instead of
    // trusting the button state from an earlier render.
    if (action === 'APPROVE' && !canExecuteSelected) return;
    // Invalid proposals are reject-only; their approval path must never reach
    // the asynchronous executor.
    if (action === 'APPROVE' && selectedHasInvalidProposal) return;
    if (action === 'REJECT' && !canExecuteRejectionSelected) return;

    // Clear only local dialog state. The helper registers the full batch
    // synchronously before this dialog can close or unmount.
    setExecutionProposalIds(selectedProposalIds);
    hasObservedExecutionRef.current = false;
    setSelectedChangeIds([]);
    startProposalBatchExecution({
      proposalIds: selectedProposalIds,
      action,
      proposalType: 'member',
      startExecution,
      executeProposal,
      onAlreadyExecuting: async () => {
        // The conflict proves the cached idle response is stale. Undefined or
        // failed refreshes leave the batch blocked until a later poll succeeds.
        hasObservedExecutionRef.current = true;
        onProposalStateUnavailable();
        await refreshProposals();
      },
      onSuccess: async () => {
        // Refresh after each success so an applied proposal remains visible even
        // when another proposal in the same batch fails.
        onApplied();
        await refreshProposals();
      },
    });
  };

  const allSelected =
    proposals.length > 0 && selectedChangeIds.length === proposals.length;

  const approveButtonDisabled = canExecuteSelected
    ? isSubmitting || isExecuteLoading || selectedHasInvalidProposal
    : selectedChangeIds.length === 0 ||
      isSubmitting ||
      selectedHasInvalidProposal;

  return (
    <FullscreenDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Review proposed changes to Members (${proposals.length})`}
      noPadding
      bodyContainerClassName="bg-muted"
    >
      <div className="p-6">
        {proposals.length === 0 ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center gap-4 text-center">
            <p className="text-sm text-muted-foreground">
              No proposed changes are waiting for review.
            </p>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : (
          <>
            <div className="flex justify-end gap-3 mb-6">
              {isProposalExecuting ? (
                <ExecutionInProgressButton />
              ) : (
                <>
                  <Button
                    size="default"
                    onClick={
                      canExecuteRejectionSelected
                        ? () => executeSelected('REJECT')
                        : () => signSelected(OrgOperationDecision.REJECT)
                    }
                    disabled={
                      canExecuteRejectionSelected
                        ? isSubmitting || isExecuteLoading
                        : selectedChangeIds.length === 0 || isSubmitting
                    }
                    className={`bg-rose-700 text-orange-100 text-shadow-sm border-orange-300 hover:bg-red-600
                      text-sm font-medium ${
                        (
                          canExecuteRejectionSelected
                            ? isSubmitting || isExecuteLoading
                            : selectedChangeIds.length === 0 || isSubmitting
                        )
                          ? 'saturate-[.2] !opacity-25'
                          : ''
                      }`}
                  >
                    {isSubmitting || isExecuteLoading ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <CircleX className="h-4 w-4 mr-1" />
                    )}
                    {canExecuteRejectionSelected
                      ? selectedChangeIds.length === 1
                        ? 'Execute rejection'
                        : `Execute rejections (${selectedChangeIds.length})`
                      : `Reject selected (${selectedChangeIds.length})`}
                  </Button>

                  <DisabledReasonTooltip reason={approveDisabledReason}>
                    <Button
                      size="default"
                      onClick={
                        canExecuteSelected
                          ? () => executeSelected('APPROVE')
                          : () => signSelected(OrgOperationDecision.APPROVE)
                      }
                      disabled={approveButtonDisabled}
                      className={`bg-green-700 text-yellow-100 text-shadow-sm border-lime-300 hover:bg-green-600
                        text-sm font-medium ${
                          approveButtonDisabled
                            ? 'saturate-[.2] !opacity-25'
                            : ''
                        }`}
                    >
                      {isSubmitting || isExecuteLoading ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <CircleCheckBig className="h-4 w-4 mr-1" />
                      )}
                      {canExecuteSelected
                        ? selectedChangeIds.length === 1
                          ? 'Execute'
                          : `Execute selected (${selectedChangeIds.length})`
                        : `Approve selected (${selectedChangeIds.length})`}
                    </Button>
                  </DisabledReasonTooltip>
                </>
              )}
            </div>

            <StyledTableWrapper>
              <StyledTable>
                <StyledTableHeader>
                  <StyledTableRow>
                    <StyledTableHead className="w-12">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={(checked) =>
                          handleSelectAll(!!checked)
                        }
                        aria-label="Select all"
                      />
                    </StyledTableHead>
                    <StyledTableHead>Change Type</StyledTableHead>
                    <StyledTableHead>Member Name</StyledTableHead>
                    <StyledTableHead>Email</StyledTableHead>
                    <StyledTableHead className="w-48"></StyledTableHead>
                    <StyledTableHead className="w-40"></StyledTableHead>
                  </StyledTableRow>
                </StyledTableHeader>
                <StyledTableBody>
                  {proposals.map((p) => (
                    <MemberProposalRow
                      key={p.proposal.id}
                      proposal={p}
                      isSelected={selectedChangeIds.includes(p.proposal.id)}
                      onSelectChange={handleSelectChange}
                    />
                  ))}
                </StyledTableBody>
              </StyledTable>
            </StyledTableWrapper>
          </>
        )}
      </div>

      <PendingSignaturesModal
        open={isPendingModalOpen}
        resetPendingState={resetPendingState}
        refreshSignables={refreshProposals}
        pendingSignableIds={pendingSignableIds}
        polledSignables={proposals.map((p) => ({
          id: p.proposal.id,
          approvals: p.approvals,
          rejections: p.rejections,
          requiredApprovals: p.requiredApprovals,
          initiatorSignature: undefined,
        }))}
        pendingDecision={pendingDecision}
        hookFunctionToPoll={usePendingMemberProposals}
        isTransaction={false}
      />
    </FullscreenDialog>
  );
}
